/**
 * USER-FACING FEATURE FLAGS HOOK
 *
 * Used by any component to check if a feature is enabled
 * for the current company. Resolves features from plan + overrides + defaults.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { FeatureFlagService, type ResolvedFeature } from '@/services/featureFlagService';

export function useFeatureFlags() {
  const { profile } = useAuthStore();
  const companyId = profile?.companyId;

  const { data: features, isLoading, error } = useQuery<ResolvedFeature[]>({
    queryKey: ['company-features', companyId],
    queryFn: () => FeatureFlagService.getCompanyFeatures(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const isFeatureEnabled = useCallback(
    (featureCode: string): boolean => {
      // Super admins have access to everything
      if (profile?.isSuperAdmin) return true;
      if (!features) return false;
      const feature = features.find((f) => f.feature_code === featureCode);
      return feature?.is_enabled ?? false;
    },
    [features, profile?.isSuperAdmin]
  );

  const getFeatureSource = useCallback(
    (featureCode: string): string | null => {
      const feature = features?.find((f) => f.feature_code === featureCode);
      return feature?.source ?? null;
    },
    [features]
  );

  const getFeatureExpiry = useCallback(
    (featureCode: string): string | null => {
      const feature = features?.find((f) => f.feature_code === featureCode);
      return feature?.expires_at ?? null;
    },
    [features]
  );

  const enabledFeatures = useMemo(
    () => features?.filter((f) => f.is_enabled).map((f) => f.feature_code) ?? [],
    [features]
  );

  return {
    features,
    isLoading,
    error,
    isFeatureEnabled,
    getFeatureSource,
    getFeatureExpiry,
    enabledFeatures,
  };
}
